import { callPath } from '../routes'
import type { PublicCall } from '../types'
import { formatR } from '../components/trackRecord/recordUI'
import { isOpenSignal } from '../components/signals/signalChips'
import { displaySymbol } from './displaySymbol'
import { formatPrice } from './marketPrice'
import { formatPnlPct } from './pnl'
import { EXIT_REASON_LABEL, exitReason } from './signalPnl'
import { formatUtcSeconds } from './utcTime'

export const callUrl = (id: number) => `${window.location.origin}${callPath(id)}`

/** A creator's call, whether or not any handle was stored with it. */
export const isCreatorCall = (call: PublicCall) => Boolean(call.creator) || call.signal?.trading_mode === 'creator'

/** The handle to name a creator call by: as locked, else the one held now; null if neither exists. */
export const callerHandle = (call: PublicCall): string | null =>
  call.creator?.handle?.trim() || call.creator?.current_handle?.trim() || null

/**
 * Who made the call. Creator or engine is decided by the call, never by
 * whether a handle exists: a creator call with no handle is still a creator's.
 * A deleted account's calls stay public under that name, not a handle.
 */
export const callerLine = (call: PublicCall) => {
  if (!isCreatorCall(call)) return 'Published by the FinCreator signal engine.'
  if (call.creator?.deleted) return 'Called by a deleted account.'
  const handle = callerHandle(call)
  return handle ? `Called by @${handle} on FinCreator.` : 'Called by a FinCreator creator.'
}

/**
 * The one text every share option posts for a call: the facts as the receipt
 * records them, who called it and the receipt link. Fixed, written by no
 * model, and the same template for a loss as for a win. Null on a sealed
 * call, whose levels are not public yet.
 *
 *   open:   "Long BTC/USDT: entry $100,000.00, stop $98,000.00, target $104,000.00, expires 2026-10-08 09:30:15 UTC."
 *   closed: "Long BTC/USDT, closed (Stop hit): -1.00R, -2.00% before fees."
 *   then:   "Called by @trader1 on FinCreator. Receipt: https://…/r/41"
 */
export const callShareText = (call: PublicCall): string | null => {
  const signal = call.signal
  if (!signal) return null
  const what = `${signal.signal_type === 'long' ? 'Long' : 'Short'} ${displaySymbol(signal.symbol)}`

  let facts: string
  if (isOpenSignal(signal)) {
    const expires = formatUtcSeconds(signal.expires_at)
    facts =
      `${what}: entry $${formatPrice(signal.entry_price)}, stop $${formatPrice(signal.stop_loss)}, ` +
      `target $${formatPrice(signal.take_profit)}${expires ? `, expires ${expires}` : ''}.`
  } else {
    const reason = EXIT_REASON_LABEL[exitReason(signal) ?? 'closed']
    const r = call.record?.gross_r
    const pct = call.record?.gross_pnl_pct
    const result = [r != null ? formatR(r) : null, pct != null ? formatPnlPct(pct) : null].filter(Boolean).join(', ')
    facts = `${what}, closed (${reason}): ${result ? `${result} before fees` : 'no result recorded'}.`
  }

  return `${facts}\n${callerLine(call)} Receipt: ${callUrl(call.id)}`
}

export const xShareUrl = (text: string) => `https://twitter.com/intent/tweet?${new URLSearchParams({ text }).toString()}`

/** LinkedIn's share-offsite takes the link only; the text goes on the clipboard to paste. */
export const linkedInShareUrl = (url: string) =>
  `https://www.linkedin.com/sharing/share-offsite/?${new URLSearchParams({ url }).toString()}`
